import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { TemplateModel } from "../../../../../factories/templates/models/template.model";
import { getLinkedFirm } from "../../../../../sessionStorage/sessionStorage";
import {
  useActivateTemplateMutation,
  useDeactivateTemplateMutation,
  useDeleteTemplateMutation,
  useDuplicateTemplatesMutation,
  useImportTemplatesMutation,
  useLazyGetTemplatesQuery,
} from "../../../../../slices/TemplatesSlice";
import { serverErrorMessage } from "../../../workTypesV2/model/workTypeEditor.utils";
import {
  TaskTemplateListStatus,
  TaskTemplatesListController,
} from "../model/taskTemplatesList.types";
import { templateImportSummary } from "../model/templateImport.utils";

export const useTaskTemplatesListController = (
  initialStatus: TaskTemplateListStatus = "all",
): TaskTemplatesListController => {
  const navigate = useNavigate();
  const orgId = getLinkedFirm()?.orgId || "";

  const [activeStatus, setActiveStatus] =
    useState<TaskTemplateListStatus>(initialStatus);
  const [openTemplateDelete, setOpenTemplateDelete] =
    useState<TemplateModel | null>(null);
  const [openTemplateDeactivate, setOpenTemplateDeactivate] =
    useState<TemplateModel | null>(null);
  const [openTemplateActivate, setOpenTemplateActivate] =
    useState<TemplateModel | null>(null);
  const [isOpenImport, setIsOpenImport] = useState(false);
  const [duplicatingTemplateId, setDuplicatingTemplateId] = useState("");
  const [duplicateTemplateName, setDuplicateTemplateName] = useState("");

  const [getTemplates, { isLoading: isLoadingTemplates, data: templates }] =
    useLazyGetTemplatesQuery();
  const [duplicateTemplates, { isLoading: isLoadingDuplicate }] =
    useDuplicateTemplatesMutation();
  const [deleteTemplate, { isLoading: isLoadingDelete }] =
    useDeleteTemplateMutation();
  const [deactivateTemplate, { isLoading: isLoadingDeactivate }] =
    useDeactivateTemplateMutation();
  const [activateTemplate, { isLoading: isLoadingActivate }] =
    useActivateTemplateMutation();
  const [importTemplates, { isLoading: isLoadingImport }] =
    useImportTemplatesMutation();

  const fetchTemplates = useCallback(async () => {
    if (!orgId) {
      return;
    }

    await getTemplates({ orgId, status: activeStatus }).unwrap();
  }, [activeStatus, getTemplates, orgId]);

  const handleStatusChange = useCallback((status: TaskTemplateListStatus) => {
    setActiveStatus(status);
  }, []);

  const handleCreate = useCallback(() => {
    navigate("/settings/templates/task-templates/view/add");
  }, [navigate]);

  const handleEdit = useCallback(
    (template: TemplateModel) => {
      navigate(`/settings/templates/task-templates/view/${template.id}`);
    },
    [navigate],
  );

  const handleImport = useCallback(
    async (file: File) => {
      if (!file || !orgId) {
        return;
      }

      const formData = new FormData();
      formData.append("formFile", file);

      try {
        const result = await importTemplates({
          orgId,
          body: formData,
        }).unwrap();

        // QA batch 16 (TPL-020): say what was imported and what was skipped.
        const summary = templateImportSummary(result);
        toast(summary.text, { type: summary.type });

        setIsOpenImport(false);
        await fetchTemplates();
      } catch (e) {
        toast(serverErrorMessage(e, "Something went wrong"), {
          type: "error",
        });
      }
    },
    [fetchTemplates, importTemplates, orgId],
  );

  const handleDeleteTemplate = useCallback(
    async (template: TemplateModel) => {
      try {
        await deleteTemplate(template).unwrap();

        toast("Template successfully deleted", {
          type: "success",
        });

        setOpenTemplateDelete(null);
        await fetchTemplates();
      } catch {
        toast(
          activeStatus === "active"
            ? "Something went wrong. You can deactivate the template to hide it"
            : "Something went wrong.",
          {
            type: "error",
          },
        );
      }
    },
    [activeStatus, deleteTemplate, fetchTemplates],
  );

  const handleDeactivateTemplate = useCallback(
    async (template: TemplateModel) => {
      try {
        await deactivateTemplate(template).unwrap();

        toast("Template successfully deactivated", {
          type: "success",
        });

        setOpenTemplateDeactivate(null);
        await fetchTemplates();
      } catch (e) {
        toast(serverErrorMessage(e, "Something went wrong"), {
          type: "error",
        });
      }
    },
    [deactivateTemplate, fetchTemplates],
  );

  const handleActivateTemplate = useCallback(
    async (template: TemplateModel) => {
      try {
        await activateTemplate(template).unwrap();

        toast("Template successfully activated", {
          type: "success",
        });

        setOpenTemplateActivate(null);
        await fetchTemplates();
      } catch (e) {
        toast(serverErrorMessage(e, "Something went wrong"), {
          type: "error",
        });
      }
    },
    [activateTemplate, fetchTemplates],
  );

  const handleOpenDuplicate = useCallback((template: TemplateModel) => {
    setDuplicatingTemplateId(template.id);
    setDuplicateTemplateName(`${template.templateName} (Copy)`);
  }, []);

  const handleCloseDuplicate = useCallback(() => {
    setDuplicatingTemplateId("");
    setDuplicateTemplateName("");
  }, []);

  const handleDuplicateTemplate = useCallback(async () => {
    if (!orgId || !duplicatingTemplateId || !duplicateTemplateName.trim()) {
      return;
    }

    try {
      await duplicateTemplates({
        orgId,
        templateId: duplicatingTemplateId,
        templateName: duplicateTemplateName.trim(),
      }).unwrap();

      toast("Template successfully duplicated", {
        type: "success",
      });

      handleCloseDuplicate();
      await fetchTemplates();
    } catch (e) {
      toast(serverErrorMessage(e, "Something went wrong"), {
        type: "error",
      });
    }
  }, [
    duplicateTemplateName,
    duplicateTemplates,
    duplicatingTemplateId,
    fetchTemplates,
    handleCloseDuplicate,
    orgId,
  ]);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    setActiveStatus(initialStatus);
  }, [initialStatus]);

  return {
    headerProps: {
      activeStatus,
      isImporting: isLoadingImport,
      onStatusChange: handleStatusChange,
      onImport: () => setIsOpenImport(true),
      onCreate: handleCreate,
    },
    tableProps: {
      isLoading: isLoadingTemplates,
      templates: templates || [],
      onEdit: handleEdit,
      onDeactivate: setOpenTemplateDeactivate,
      onActivate: setOpenTemplateActivate,
      onDuplicate: handleOpenDuplicate,
      onDelete: setOpenTemplateDelete,
    },
    confirmModalProps: {
      deleteTemplate: openTemplateDelete,
      deactivateTemplate: openTemplateDeactivate,
      activateTemplate: openTemplateActivate,
      isDeleting: isLoadingDelete,
      isDeactivating: isLoadingDeactivate,
      isActivating: isLoadingActivate,
      onCloseDelete: () => setOpenTemplateDelete(null),
      onCloseDeactivate: () => setOpenTemplateDeactivate(null),
      onCloseActivate: () => setOpenTemplateActivate(null),
      onConfirmDelete: () =>
        openTemplateDelete && void handleDeleteTemplate(openTemplateDelete),
      onConfirmDeactivate: () =>
        openTemplateDeactivate &&
        void handleDeactivateTemplate(openTemplateDeactivate),
      onConfirmActivate: () =>
        openTemplateActivate &&
        void handleActivateTemplate(openTemplateActivate),
    },
    importModalProps: {
      isOpen: isOpenImport,
      onClose: () => setIsOpenImport(false),
      onFileDrop: (file) => void handleImport(file),
    },
    duplicateModalProps: {
      isOpen: Boolean(duplicatingTemplateId),
      templateName: duplicateTemplateName,
      isDuplicating: isLoadingDuplicate,
      onTemplateNameChange: setDuplicateTemplateName,
      onClose: handleCloseDuplicate,
      onConfirm: () => void handleDuplicateTemplate(),
    },
  };
};
